import {
  Box,
  Button,
  DashboardModule,
  Form,
  Image,
  LoadingModule,
  SaveFormModule,
  SidebarNoticeBox,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useAgency, { useAgencyForm } from '@hooks/useAgency'
import useAgencySelector from '@hooks/useAgencySelector'
import useAttachments from '@hooks/useAttachments'
import useBrand from '@hooks/useBrand'
import useCarousel from '@hooks/useCarousel'

import handleCallbackAction from '@functions/handleCallbackAction'

import CarouselControls from '@components/CarouselControls'
import Watermark from '@components/Watermark'

import watermarkBaseImage1 from '../../assets/watermarkBase1.jpg'
import watermarkBaseImage2 from '../../assets/watermarkBase2.jpg'
import watermarkBaseImage3 from '../../assets/watermarkBase3.jpg'
import watermarkBaseImage4 from '../../assets/watermarkBase4.jpg'

import WatermarkImage from '../WatermarkImage'
import WatermarkSettingToggles from '../WatermarkSettingToggles'

const imageUrls = [watermarkBaseImage1, watermarkBaseImage2, watermarkBaseImage3, watermarkBaseImage4]

const WaterMarkSettings = () => {
  const { selectedAgencyId } = useAgencySelector()

  const agencyPayload = useAgency({ id: selectedAgencyId })
  const {
    agency,
    callbacks: { updateAgency, updateWatermarkImage },
    hasBrandImageSettings,
    updating,
    loading: loadingAgency,
  } = agencyPayload

  // @ts-expect-error TS2345
  const formPayload = useAgencyForm(agency, { validateOn: 'change' })
  const {
    entityState,
    entityState: {
      options: {
        export_settings,
        watermark_settings,
      },
    },
    handlers,
  } = formPayload

  const { loading: loadingBrand } = useBrand()

  const horizontal = digObject(watermark_settings, 'padding.horizontal', 20)
  const opacity = digObject(watermark_settings, 'opacity', 0.7)
  const position = digObject(watermark_settings, 'position', 'south-east')
  const vertical = digObject(watermark_settings, 'padding.vertical', 20)

  const brandHorizontal = digObject(agency, 'brand_image_settings.watermark_settings.padding.horizontal', 12)
  const brandOpacity = digObject(agency, 'brand_image_settings.watermark_settings.opacity', 0.7)
  const brandPosition = digObject(agency, 'brand_image_settings.watermark_settings.position', 'south-east')
  const brandVertical = digObject(agency, 'brand_image_settings.watermark_settings.padding.vertical', 12)

  const { filteredAttachments } = useAttachments({
    filters: {
      // @ts-expect-error TS2339
      attachable_id: agency.id,
      attachable_type: 'Agency',
      attachment_type: 'watermark_image',
    },
  })

  const watermarkImage = filteredAttachments[0] || {}

  const watermarkImageUrl = digObject(watermarkImage, 'links.original_url')

  const brandWatermarkImage = digObject(agency, 'brand_watermark_attachment', {})
  const brandWatermarkImageUrl = digObject(brandWatermarkImage, 'links.original_url', '')

  const exportSize = digObject(export_settings, 'size', 'original')
  const height = digObject(export_settings, 'height')
  const width = digObject(export_settings, 'width')

  const brandExportSize = digObject(agency, 'brand_image_settings.export_settings.size', 'original')
  const brandHeight = digObject(agency, 'brand_image_settings.export_settings.height')
  const brandWidth = digObject(agency, 'brand_image_settings.export_settings.width')

  const watermarkDisplaySettings = {
    height: hasBrandImageSettings ? digObject(brandWatermarkImage, 'dimensions.original.height', 200)
      : digObject(watermarkImage, 'dimensions.original.height', 200),
    horizontal: hasBrandImageSettings ? brandHorizontal : horizontal,
    opacity: hasBrandImageSettings ? brandOpacity : opacity,
    position: hasBrandImageSettings ? brandPosition : position,
    vertical: hasBrandImageSettings ? brandVertical : vertical,
    width: hasBrandImageSettings ? digObject(brandWatermarkImage, 'dimensions.original.width', 200)
      : digObject(watermarkImage, 'dimensions.original.width', 200),
  }

  const brandWatermarkImagesOnExport = digObject(agency, 'brand_image_settings.watermark_images', [])

  const isCustomExportSize = (
    (!hasBrandImageSettings && exportSize === 'custom')
    || (hasBrandImageSettings && brandExportSize === 'custom')
  )

  const updateAgencyPayload = {
    callbacks: {
      action: updateAgency,
    },
    entityParams: entityState,
    toastText: 'Watermark Settings updated',
  }

  const carouselPayload = useCarousel({ initialIndex: 0, items: imageUrls })
  const { selectedIndex } = carouselPayload

  // @ts-expect-error TS2741
  if (loadingAgency || loadingBrand) return <LoadingModule times={3} />

  return (
    <>
      <DashboardModule title="Image Export Settings">
        <Form padding="large">
          <Box justifyContent="center">
            <Box position="relative" maxWidth={600} maxHeight={[300, 400]}>
              <Image
                borderRadius="5px"
                height={[250, 400]}
                // @ts-expect-error TS2538
                url={imageUrls[selectedIndex]}
              />

              <CarouselControls carouselPayload={carouselPayload} />

              <Watermark
                watermarkImageUrl={hasBrandImageSettings ? brandWatermarkImageUrl : watermarkImageUrl}
                watermarkSettings={watermarkDisplaySettings}
              />
            </Box>
          </Box>

          <Form.Row marginTop="large">
            <Form.Field label="Export Size">
              {/*
               // @ts-expect-error TS2322 */}
              <select
                disabled={hasBrandImageSettings}
                name="options.export_settings.size"
                value={hasBrandImageSettings ? brandExportSize : exportSize}
                {...handlers}
              >
                <option value="original">Original</option>
                <option value="custom">Custom Size</option>
              </select>
            </Form.Field>

            {isCustomExportSize && (
              // @ts-expect-error TS2322
              <Form.Row marginTop={['large', 0]}>
                <Form.Field label="Width">
                  <input
                    disabled={hasBrandImageSettings}
                    name="options.export_settings.width"
                    type="number"
                    value={hasBrandImageSettings ? brandWidth : width}
                    {...handlers}
                  />
                </Form.Field>

                {/*
                 // @ts-expect-error TS2322 */}
                <Form.Field label="Height" marginTop={['large', 0]}>
                  <input
                    disabled={hasBrandImageSettings}
                    name="options.export_settings.height"
                    type="number"
                    value={hasBrandImageSettings ? brandHeight : height}
                    {...handlers}
                  />
                </Form.Field>
              </Form.Row>
            )}
          </Form.Row>

          {!hasBrandImageSettings && (
            <SidebarNoticeBox boxProps={{ marginTop: 'large' }}>
              <SidebarNoticeBox.Title>Watermark Recommendation</SidebarNoticeBox.Title>

              <SidebarNoticeBox.Paragraph>
                Realestate.com.au and Domain.com.au watermark specifications recommend pinning your watermark to the
                bottom right. Padding and size of watermark will vary based on the final image output dimensions.
              </SidebarNoticeBox.Paragraph>
            </SidebarNoticeBox>
          )}

          <Form.Row marginTop={hasBrandImageSettings ? 'large' : ''}>
            <Form.Field label="Pin to Corner">
              {/*
               // @ts-expect-error TS2322 */}
              <select
                disabled={hasBrandImageSettings}
                name="options.watermark_settings.position"
                value={hasBrandImageSettings ? brandPosition : position}
                {...handlers}
              >
                <option value="south-east">Bottom Right</option>
                <option value="south-west">Bottom Left</option>
                <option value="north-east">Top Right</option>
                <option value="north-west">Top Left</option>
              </select>
            </Form.Field>

            {/*
             // @ts-expect-error TS2322 */}
            <Form.Row marginTop={['large', 0]}>
              <Form.Field label="Padding (H)">
                <input
                  disabled={hasBrandImageSettings}
                  name="options.watermark_settings.padding.horizontal"
                  type="number"
                  value={hasBrandImageSettings ? brandHorizontal : horizontal}
                  {...handlers}
                />
              </Form.Field>

              {/*
               // @ts-expect-error TS2322 */}
              <Form.Field label="Padding (V)" marginTop={['large', 0]}>
                <input
                  disabled={hasBrandImageSettings}
                  name="options.watermark_settings.padding.vertical"
                  type="number"
                  value={hasBrandImageSettings ? brandVertical : vertical}
                  {...handlers}
                />
              </Form.Field>
            </Form.Row>
          </Form.Row>

          {/*
           // @ts-expect-error TS2322 */}
          <Form.Row padding="large">
            {/*
             // @ts-expect-error TS2322 */}
            {!hasBrandImageSettings && <WatermarkImage callbacks={{ updateWatermarkImage }} />}

            <Form.Field label="Transparency" marginTop="large">
              {/*
               // @ts-expect-error TS2322 */}
              <select
                disabled={hasBrandImageSettings}
                name="options.watermark_settings.opacity"
                value={hasBrandImageSettings ? brandOpacity : opacity}
                {...handlers}
              >
                <option value="0.1">10%</option>
                <option value="0.2">20%</option>
                <option value="0.3">30%</option>
                <option value="0.4">40%</option>
                <option value="0.5">50%</option>
                <option value="0.6">60%</option>
                <option value="0.7">70%</option>
                <option value="0.8">80%</option>
                <option value="0.9">90%</option>
                <option value="1.0">100%</option>
              </select>
            </Form.Field>
          </Form.Row>

          {watermarkImageUrl && !hasBrandImageSettings && (
            <Form.Field label="Current Watermark" marginTop="large">
              <Image
                backgroundSize="contain"
                boxProps={{ variant: 'whiteGrey' }}
                height={240}
                placeholderUrl=""
                url={watermarkImageUrl}
                width="50%"
              />
            </Form.Field>
          )}

          {brandWatermarkImageUrl && hasBrandImageSettings && (
            <Form.Field label="Brand Watermark" marginTop="large">
              <Image
                backgroundSize="contain"
                boxProps={{ variant: 'whiteGrey' }}
                height={240}
                placeholderUrl=""
                url={brandWatermarkImageUrl}
                width="50%"
              />
            </Form.Field>
          )}

          <WatermarkSettingToggles
            // @ts-expect-error TS2322
            agency={agency}
            brandWatermarkImages={brandWatermarkImagesOnExport}
            callbacks={{ updateAgency }}
            hasBrandImageSettings={hasBrandImageSettings}
          />
        </Form>
      </DashboardModule>

      <SaveFormModule>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          marginLeft="auto"
          // @ts-expect-error TS2345
          onClick={() => handleCallbackAction(updateAgencyPayload)}
          size="medium"
        >
          Save Details
        </Button>
      </SaveFormModule>
    </>
  )
}

export default WaterMarkSettings
